/**
 * Supabase Image Transformations utility
 * 
 * Uses Supabase's built-in image transformation service (Pro plan feature)
 * to automatically optimize images on-the-fly via URL parameters.
 * 
 * Benefits:
 * - Automatic WebP conversion based on browser support
 * - CDN caching of transformed images
 * - No server-side processing needed
 * 
 * Docs: https://supabase.com/docs/guides/storage/serving/image-transformations
 */

const SUPABASE_URL = 'https://wstncgluifpnevsejrwz.supabase.co';

export interface TransformOptions {
  width?: number;
  height?: number;
  quality?: number;
}

/**
 * Predefined image presets for consistent optimization across the app
 * targetWidth = CSS pixel width (will be doubled for @2x retina)
 */
export const IMAGE_PRESETS = {
  /** Cards in catalogs (400px CSS = 800px @2x) */
  card: { targetWidth: 400, quality: 75 },
  /** Gallery thumbnails (600px CSS = 1200px @2x) */
  gallery: { targetWidth: 600, quality: 75 },
  /** Hero sections (1200px CSS = 2400px @2x) */
  hero: { targetWidth: 1200, quality: 80 },
  /** Admin/upload previews (200px CSS = 400px @2x) */
  thumbnail: { targetWidth: 200, quality: 70 },
  /** Lightbox fullscreen (1600px CSS = 3200px @2x) */
  lightbox: { targetWidth: 1600, quality: 85 },
} as const;

/**
 * Converts Supabase Storage URL to render/image URL
 */
function toRenderUrl(originalUrl: string): string {
  return originalUrl.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );
}

/**
 * Gets a responsive image URL with proper aspect ratio preservation.
 * Calculates proportional height based on original dimensions and applies @2x for retina.
 * 
 * @param originalUrl - The original Supabase Storage URL
 * @param originalWidth - Original image width from database
 * @param originalHeight - Original image height from database
 * @param targetWidth - Desired CSS display width in pixels
 * @param quality - Image quality 1-100 (default: 75)
 * @param retinaScale - Scale factor for HiDPI displays (default: 2 for @2x)
 * @returns Transformed URL with both width and height parameters
 * 
 * @example
 * // For a 4000x2048 image displayed at 400px CSS width:
 * getResponsiveImageUrl(url, 4000, 2048, 400)
 * // Returns: .../render/image/public/...?width=800&height=410&quality=75
 */
export function getResponsiveImageUrl(
  originalUrl: string | null | undefined,
  originalWidth: number | null | undefined,
  originalHeight: number | null | undefined,
  targetWidth: number,
  quality: number = 75,
  retinaScale: number = 2
): string {
  if (!originalUrl) return '';
  
  // Only transform Supabase Storage URLs
  if (!originalUrl.includes(SUPABASE_URL)) {
    return originalUrl;
  }

  const renderUrl = toRenderUrl(originalUrl);
  const params = new URLSearchParams();
  
  // Calculate @2x dimensions
  const requestWidth = Math.round(targetWidth * retinaScale);
  params.set('width', String(requestWidth));
  
  // If we have original dimensions, calculate proportional height
  if (originalWidth && originalHeight && originalWidth > 0) {
    const aspectRatio = originalHeight / originalWidth;
    const requestHeight = Math.round(requestWidth * aspectRatio);
    params.set('height', String(requestHeight));
  }
  
  params.set('quality', String(quality));
  
  return `${renderUrl}?${params.toString()}`;
}

/**
 * Gets responsive image URL using a preset configuration.
 * 
 * @example
 * getResponsiveImage(url, 4000, 2048, 'card')
 */
export function getResponsiveImage(
  originalUrl: string | null | undefined,
  originalWidth: number | null | undefined,
  originalHeight: number | null | undefined,
  preset: keyof typeof IMAGE_PRESETS
): string {
  const config = IMAGE_PRESETS[preset];
  return getResponsiveImageUrl(
    originalUrl,
    originalWidth,
    originalHeight,
    config.targetWidth,
    config.quality
  );
}

/**
 * Gets an optimized image URL with quality setting only.
 * Use this when original dimensions are NOT available (e.g., gallery images).
 * The image is served in WebP with reduced quality for smaller file size,
 * but the original aspect ratio and dimensions are preserved (no cropping).
 */
export function getQualityOptimizedUrl(
  originalUrl: string | null | undefined,
  quality: number = 75
): string {
  if (!originalUrl) return '';

  if (!originalUrl.includes(SUPABASE_URL)) {
    return originalUrl;
  }

  const renderUrl = toRenderUrl(originalUrl);
  return `${renderUrl}?quality=${quality}`;
}

/**
 * Gets an optimized image URL with an explicit width (single variant) and quality.
 * Used for gallery thumbnails where original aspect ratio must be preserved
 * but we want to cap the rendered width so we don't ship 1500+ px images to mobile.
 */
export function getSizedOptimizedUrl(
  originalUrl: string | null | undefined,
  width: number,
  quality: number = 75
): string {
  if (!originalUrl) return '';
  if (!originalUrl.includes(SUPABASE_URL)) return originalUrl;

  const renderUrl = toRenderUrl(originalUrl);
  const params = new URLSearchParams();
  params.set('width', String(width));
  params.set('quality', String(quality));
  return `${renderUrl}?${params.toString()}`;
}

/**
 * Generates a responsive image `srcset` (1x/2x density descriptors) for a single
 * layout slot of given CSS width. Quality defaults to 75 which is a good tradeoff
 * between visual quality and file size for WebP output from Supabase.
 */
export function getResponsiveSrcSet(
  originalUrl: string | null | undefined,
  cssWidth: number,
  quality: number = 75
): string {
  if (!originalUrl) return '';
  if (!originalUrl.includes(SUPABASE_URL)) return originalUrl;

  const x1 = getSizedOptimizedUrl(originalUrl, cssWidth, quality);
  const x2 = getSizedOptimizedUrl(originalUrl, cssWidth * 2, quality);
  return `${x1} 1x, ${x2} 2x`;
}

/**
 * Builds a `srcset` with width descriptors (e.g. "url1 400w, url2 800w, ...")
 * and returns both the srcset and a recommended fallback src. Pass a list of
 * candidate widths in CSS pixels — callers supply a matching `sizes` attribute.
 */
export function buildWidthSrcSet(
  originalUrl: string | null | undefined,
  widths: number[],
  quality: number = 75
): { src: string; srcSet: string } {
  if (!originalUrl) return { src: '', srcSet: '' };
  if (!originalUrl.includes(SUPABASE_URL)) {
    return { src: originalUrl, srcSet: '' };
  }
  const sorted = [...widths].sort((a, b) => a - b);
  const srcSet = sorted
    .map((w) => `${getSizedOptimizedUrl(originalUrl, w, quality)} ${w}w`)
    .join(', ');
  return { src: getSizedOptimizedUrl(originalUrl, sorted[sorted.length - 1], quality), srcSet };
}

// ============================================
// Legacy functions - kept for backward compatibility
// Use getResponsiveImageUrl for new code when dimensions are available
// ============================================

/** @deprecated Use getResponsiveImageUrl when you have original dimensions */
export const IMAGE_SIZES = {
  hero: { width: 1920, quality: 75 },
  card: { width: 600, quality: 75 },
  gallery: { width: 800, quality: 75 },
  thumbnail: { width: 400, quality: 75 },
  lightbox: { width: 1920, quality: 85 },
} as const;

/**
 * @deprecated Use getResponsiveImageUrl when you have original dimensions.
 * This function only sets width, which can cause aspect ratio issues with Supabase.
 */
export function getOptimizedImageUrl(
  originalUrl: string | null | undefined, 
  options: TransformOptions = {}
): string {
  if (!originalUrl) return '';
  
  if (!originalUrl.includes(SUPABASE_URL)) {
    return originalUrl;
  }

  const renderUrl = toRenderUrl(originalUrl);
  const params = new URLSearchParams();
  if (options.width) params.set('width', String(options.width));
  if (options.height) params.set('height', String(options.height));
  if (options.quality) params.set('quality', String(options.quality));

  const queryString = params.toString();
  return queryString ? `${renderUrl}?${queryString}` : renderUrl;
}

/**
 * @deprecated Use getResponsiveImage when you have original dimensions
 */
export function getOptimizedImage(
  originalUrl: string | null | undefined,
  preset: keyof typeof IMAGE_SIZES
): string {
  return getOptimizedImageUrl(originalUrl, IMAGE_SIZES[preset]);
}
